const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/

/** A colour as the API stores it — `#`, six digits — and the one rule for «chosen»: the colour
 *  field paints its swatch by it, a kit's drawing paints a part, and the words beside it agree. */
export function isHexColour(value: string | null | undefined): boolean {
  return value != null && HEX_COLOUR.test(value)
}

/** The two tones a figure on a coloured ground takes: the brand's white and its ink. They are the
 *  theme's `--light` and `--dark`, restated because contrast is computed here, in code; a test
 *  reads the theme and fails if they drift. */
export const NUMBER_TONES = { light: '#FFFFFF', dark: '#0A0B08' } as const

export type NumberTone = keyof typeof NUMBER_TONES

function luminance(hex: string): number {
  const [r = 0, g = 0, b = 0] = [1, 3, 5].map((at) => {
    const channel = parseInt(hex.slice(at, at + 2), 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** The WCAG contrast ratio of two `#RRGGBB` colours, from 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [lighter = 0, darker = 0] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (lighter + 0.05) / (darker + 0.05)
}

/** The tone of a figure drawn on a ground of this colour: whichever of the two reads better. */
export function numberToneFor(ground: string): NumberTone {
  const onLight = contrastRatio(ground, NUMBER_TONES.light)
  return onLight >= contrastRatio(ground, NUMBER_TONES.dark) ? 'light' : 'dark'
}
